import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MessageModule } from 'primeng/message';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MessageService } from 'primeng/api';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import type { LocationFeedbackSettings } from '@/cloud/models/feedback.models';
import { TranslationService } from '@/layout/service/translation.service';
import { type RoomFeedbackChoice, feedbackChoice, feedbackOverride } from '@/shared/feedback/room-feedback-choice';

/**
 * A room's ratings setting on its Settings tab: same as the tenant, on, or off. The room's processor picks the change
 * up within seconds; touch panels and the control program then show or hide their rating screen.
 */
@Component({
    selector: 'app-location-feedback-settings',
    standalone: true,
    imports: [FormsModule, MessageModule, SelectButtonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (loadFailed()) {
            <p-message severity="warn">{{ t()('feedbackSettings.loadError') }}</p-message>
        } @else if (settings(); as s) {
            <div class="room-feedback">
                <p-selectbutton
                    [options]="options()"
                    optionLabel="label"
                    optionValue="value"
                    [allowEmpty]="false"
                    [ngModel]="choice()"
                    (ngModelChange)="select($event)"
                    [disabled]="saving() || !canManage()"
                    [ariaLabel]="t()('feedbackSettings.title')"
                />
                @if (!canManage()) {
                    <p class="room-feedback__meta">{{ t()('feedbackSettings.roomReadOnly') }}</p>
                }
            </div>
        }
    `,
    styles: [
        `
            .room-feedback {
                display: grid;
                gap: 0.75rem;
            }
            .room-feedback__meta {
                margin: 0;
                font-size: 0.8125rem;
                color: var(--text-color-secondary);
            }
        `
    ]
})
export class LocationFeedbackSettingsComponent {
    private readonly api = inject(CloudApiService);
    private readonly messages = inject(MessageService);
    readonly t = inject(TranslationService).t;

    readonly locationId = input.required<string>();
    readonly canManage = input(false);
    /** The effective value after a change, so the room view can show or hide its "ratings are off" notice. */
    readonly effectiveChange = output<boolean>();

    readonly settings = signal<LocationFeedbackSettings | null>(null);
    readonly loadFailed = signal(false);
    readonly saving = signal(false);
    readonly choice = computed(() => feedbackChoice(this.settings()?.override));
    readonly options = computed(() => {
        const t = this.t();
        const inherited = this.settings()?.inherited ?? true;
        return [
            { value: 'inherit' as RoomFeedbackChoice, label: t('feedbackSettings.inherit').replace('{state}', t(inherited ? 'feedbackSettings.on' : 'feedbackSettings.off')) },
            { value: 'on' as RoomFeedbackChoice, label: t('feedbackSettings.on') },
            { value: 'off' as RoomFeedbackChoice, label: t('feedbackSettings.off') }
        ];
    });

    private request = 0;

    constructor() {
        effect(() => {
            const id = this.locationId();
            untracked(() => void this.load(id));
        });
        // A tenant default changed: what "Same as tenant" means (and the effective value) may have changed with it.
        this.api.feedbackSettingsChanged$?.pipe(takeUntilDestroyed()).subscribe(() => {
            if (!this.saving()) void this.load(this.locationId());
        });
    }

    async select(choice: RoomFeedbackChoice): Promise<void> {
        if (!this.canManage() || this.saving() || choice === this.choice()) return;
        const previous = this.settings();
        this.saving.set(true);
        try {
            const result = await this.api.setLocationFeedbackEnabled(this.locationId(), feedbackOverride(choice));
            this.settings.set(result);
            this.effectiveChange.emit(result.effective);
            this.messages.add({
                severity: 'success',
                summary: this.t()(result.effective ? 'feedbackSettings.savedOn' : 'feedbackSettings.savedOff'),
                detail: this.t()('feedbackSettings.savedDetail')
            });
        } catch (err) {
            // Re-render the buttons at the stored value.
            this.settings.set(previous ? { ...previous } : previous);
            const e = err as { error?: { error?: string } | string; message?: string };
            const detail = (typeof e?.error === 'object' ? e.error?.error : e?.error) ?? e?.message;
            this.messages.add({ severity: 'error', summary: this.t()('feedbackSettings.saveError'), detail });
        } finally {
            this.saving.set(false);
        }
    }

    private async load(id: string): Promise<void> {
        const request = ++this.request;
        this.settings.set(null);
        this.loadFailed.set(false);
        try {
            const settings = await this.api.getLocationFeedbackSettings(id);
            if (request === this.request) this.settings.set(settings);
        } catch {
            if (request === this.request) this.loadFailed.set(true);
        }
    }
}
